/* ==========================================================================
   Prescribe Again Modal
   Shows prescription summary and lets provider choose same or different patient
   ========================================================================== */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { usePortalSlug } from '@/context/PortalContext'
import { getProviderPrescriptionDetail } from '@/api/portal'
import { Loader2, AlertTriangle } from 'lucide-react'
import type { PrescriptionListItem } from '@/api/types'

interface PrescribeAgainModalProps {
  open: boolean
  onClose: () => void
  prescription: PrescriptionListItem | null
}

export function PrescribeAgainModal({
  open,
  onClose,
  prescription,
}: PrescribeAgainModalProps) {
  const slug = usePortalSlug()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!prescription) return null

  const isHuman = prescription.patient_type === 'human'
  const patientName = isHuman
    ? `${prescription.patient_first_name || ''} ${prescription.patient_last_name || ''}`.trim()
    : prescription.animal_name || 'Unknown'

  const productUnavailable = !prescription.product_code

  async function handlePrescribeAgain(samePatient: boolean) {
    if (!prescription) return

    setLoading(true)
    setError(null)

    try {
      const detail = await getProviderPrescriptionDetail(slug, prescription.id)

      navigate(`/rx/${slug}/prescribe`, {
        state: {
          prefill: {
            samePatient,
            catalogProductId: detail.catalog_product_id,
            drugName: detail.drug_name,
            // Patient data
            patientType: detail.patient_type,
            patientFirstName: detail.patient_first_name,
            patientLastName: detail.patient_last_name,
            patientMiddleName: detail.patient_middle_name,
            patientDob: detail.patient_dob,
            patientGender: detail.patient_gender,
            patientPhone: detail.patient_phone,
            patientEmail: detail.patient_email,
            patientAddress: detail.patient_address,
            patientAllergies: detail.patient_allergies,
            noKnownAllergies: detail.no_known_allergies,
            currentMedications: detail.current_medications,
            noCurrentMedications: detail.no_current_medications,
            // Animal patient data
            animalName: detail.animal_name,
            animalSpecies: detail.animal_species,
            animalBreed: detail.animal_breed,
            ownerName: detail.owner_name,
            ownerPhone: detail.owner_phone,
            patientWeight: detail.patient_weight,
            patientWeightUnit: detail.patient_weight_unit,
            animalDobUnknown: detail.animal_dob_unknown,
            // Prescription details
            doseSpecificDirections: detail.dose_specific_directions,
            quantity: detail.quantity,
            unitAmount: detail.unit_amount || detail.quantity_unit,
            daysSupply: detail.days_supply,
            refills: detail.refills,
            // Custom field values
            customFieldValues: detail.custom_field_values,
            generalCustomFieldValues: detail.general_custom_field_values ?? null,
            // Diagnosis, payment, delivery
            diagnosisCode: detail.diagnosis_code,
            paymentType: detail.payment_type,
            deliveryMethod: detail.delivery_method,
          },
        },
      })

      onClose()
    } catch {
      setError('Failed to load prescription details. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Prescribe Again" size="sm">
      <div className="space-y-4">
        {/* Prescription summary */}
        <div className="p-3 bg-[var(--color-gray-50)] rounded-md">
          <p className="text-sm font-medium text-[var(--color-charcoal)]">
            {prescription.drug_name}
          </p>
          <p className="text-sm text-[var(--color-gray-600)] mt-1">
            Patient: {patientName}
          </p>
        </div>

        {/* Product unavailable warning */}
        {productUnavailable && (
          <div className="flex items-start gap-2 p-3 bg-[var(--color-warning-light)] rounded-md">
            <AlertTriangle className="h-4 w-4 text-[var(--color-warning)] mt-0.5 flex-shrink-0" />
            <p className="text-sm text-[var(--color-warning)]">
              The original product is no longer available. You will need to select a product manually.
            </p>
          </div>
        )}

        {error && (
          <p className="text-sm text-[var(--color-error)]">{error}</p>
        )}

        {/* Action buttons */}
        <div className="flex flex-col gap-3">
          <Button
            variant="primary"
            onClick={() => handlePrescribeAgain(true)}
            disabled={loading}
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" aria-hidden="true" />
                Loading...
              </>
            ) : (
              'Same Patient'
            )}
          </Button>
          <Button
            variant="secondary"
            onClick={() => handlePrescribeAgain(false)}
            disabled={loading}
          >
            Different Patient
          </Button>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  )
}

export default PrescribeAgainModal
